import { 
    CHART_BREAKPOINTS, 
    CHART_LEGEND_DEFAULTS, 
    CHART_TITLE_DEFAULTS, 
    CHART_DEFAULTS,
    CHART_CONTAINER
} from './chartConstants';
import { 
    getResponsivePadding, 
    createLegendHoverHandlers, 
    createTooltipCallbacks, 
    generateLegendLabels,
    createLegendHighlightHandler
} from './chartUtils';

// Detect if dark theme is active (Chart.js renders on Canvas, so CSS can't affect text colors)
// Fallback: read DOM attribute when isDark is not passed from React context
const isDarkThemeFromDOM = () => document.documentElement.getAttribute('data-theme') === 'dark';
const getChartTextColor = (dark?: boolean) => (dark ?? isDarkThemeFromDOM()) ? 'rgba(255, 255, 255, 0.85)' : undefined;
const getChartSecondaryTextColor = (dark?: boolean) => (dark ?? isDarkThemeFromDOM()) ? 'rgba(255, 255, 255, 0.65)' : undefined;

export interface ChartConfigOptions {
    title: string;
    legendPosition?: 'left' | 'right' | 'top' | 'bottom';
    legendAlign?: 'start' | 'center' | 'end';
    chartLabels: string[];
    customLegendConfig?: any;
    customTitleConfig?: any;
    onClick: (event: any, elements: any[]) => void;
    isDark?: boolean;
}

// Factory function to create base chart options
export const createBaseChartOptions = (options: ChartConfigOptions) => {
    const screenWidth = window.innerWidth;
    const textColor = getChartTextColor(options.isDark);
    const secondaryTextColor = getChartSecondaryTextColor(options.isDark);
    const hoverHandlers = createLegendHoverHandlers(options.chartLabels);
    const tooltipCallbacks = createTooltipCallbacks(options.chartLabels);
    const highlightHandler = createLegendHighlightHandler();
    
    return {
        responsive: CHART_DEFAULTS.RESPONSIVE,
        maintainAspectRatio: CHART_DEFAULTS.MAINTAIN_ASPECT_RATIO,
        aspectRatio: CHART_DEFAULTS.ASPECT_RATIO,
        onClick: options.onClick,
        onHover: (event: any, elements: any[]) => {
            event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        },
        layout: {
            padding: getResponsivePadding(screenWidth)
        },
        plugins: {
            legend: {
                position: options.legendPosition || 'left' as const,
                align: options.legendAlign || 'center' as const,
                onClick: highlightHandler,
                ...hoverHandlers,
                labels: {
                    padding: CHART_LEGEND_DEFAULTS.PADDING,
                    usePointStyle: CHART_LEGEND_DEFAULTS.USE_POINT_STYLE,
                    color: textColor,
                    font: {
                        size: CHART_LEGEND_DEFAULTS.FONT_SIZE
                    },
                    boxWidth: CHART_LEGEND_DEFAULTS.BOX_WIDTH,
                    maxWidth: screenWidth < CHART_BREAKPOINTS.MOBILE ? 
                        CHART_LEGEND_DEFAULTS.MAX_WIDTH.MOBILE : 
                        CHART_LEGEND_DEFAULTS.MAX_WIDTH.DESKTOP,
                    generateLabels: function(chart: any) {
                        return generateLegendLabels(chart, screenWidth, options.isDark);
                    },
                    ...options.customLegendConfig
                }
            },
            tooltip: {
                callbacks: tooltipCallbacks
            },
            title: {
                display: CHART_TITLE_DEFAULTS.DISPLAY,
                text: options.title,
                color: textColor,
                font: {
                    size: CHART_TITLE_DEFAULTS.FONT_SIZE,
                },
                padding: {
                    bottom: CHART_TITLE_DEFAULTS.BOTTOM_PADDING
                },
                position: 'top' as const,
                ...options.customTitleConfig
            }
        }
    };
};

// Function to create responsive container styles
export const createContainerStyles = (customStyles?: any) => {
    const screenWidth = window.innerWidth;
    
    return {
        height: screenWidth < CHART_BREAKPOINTS.MOBILE ? 
            `${CHART_CONTAINER.HEIGHT.MOBILE}px` : 
            `${CHART_CONTAINER.HEIGHT.DESKTOP}px`,
        width: '100%',
        maxWidth: screenWidth < CHART_BREAKPOINTS.LARGE ? 
            `${CHART_CONTAINER.MAX_WIDTH.MEDIUM}px` : 
            `${CHART_CONTAINER.MAX_WIDTH.LARGE}px`,
        margin: '0 auto',
        ...customStyles
    };
};
